import React, { FC, useState } from 'react';
import { Button, Collapse, Paper, useTheme } from '@mui/material';
import partition from 'lodash/partition';
import moment from 'moment-timezone';
import { useSelector } from 'react-redux';

import { ITimecardDetailDTO, ITimecardSummaryItemDTO } from 'src/api/dtos';
import { CalendarDateIcon, ChevronDownIcon, ClockIcon, DollarSignFilledIcon } from 'src/shared/components';
import { DAY_COMMA_DATE_FORMAT, HOURS_DURATION_FORMAT, TIME_DURATION_FORMAT } from 'src/shared/constants';
import { DurationFormatEnums } from 'src/shared/enums';
import { useTranslate } from 'src/shared/providers';
import { formatDurationTimeSpan, formatTime, formatDuration } from 'src/shared/utils';
import { getTimeFormat } from 'src/store/user/user.selectors';
import './TimecardDetail.styles.scss';

interface Props {
  detail: ITimecardDetailDTO;
  className?: string;
}

export const TimecardDetail: FC<Props> = ({ detail, className }) => {
  const { t } = useTranslate();
  const theme = useTheme();

  const [openDetails, setOpenDetails] = useState<boolean>(false);

  const [premiumCategories, otherCategories] = partition<ITimecardSummaryItemDTO>(
    detail.dailySummaryItems,
    ({ isPremium }) => isPremium,
  );

  const [memoCategories, categories] = partition<ITimecardSummaryItemDTO>(otherCategories, ({ isMemo }) => isMemo);

  const openDetailsButtonText = !openDetails ? t('timecards.seeDetails') : t('timecards.hideDetails');

  return (
    <Paper elevation={4} className={`me-3 overflow-auto ${className}`}>
      <table className="w-100 timecard-table">
        <thead>
          <tr>
            <th scope="col">
              <div className="header text-muted d-flex align-items-center justify-content-center">
                <CalendarDateIcon size={20} />
                <p className="ms-1 mb-0">{t('global.date')}</p>
              </div>
              <span>{moment(detail.date).format(DAY_COMMA_DATE_FORMAT)}</span>
            </th>
            <th scope="col">
              <div className="header text-muted d-flex align-items-center justify-content-center">
                <ClockIcon size={20} />
                <p className="ms-1 mb-0">
                  {t('timecards.in')} - {t('timecards.out')}
                </p>
              </div>
              <span>
                <TimecardTimeSpanValue startTimeSpan={detail.in} endTimeSpan={detail.out} />
              </span>
            </th>
            <th scope="col">
              <div className="header text-muted d-flex align-items-center justify-content-center">
                <ClockIcon size={20} />
                <p className="ms-1 mb-0">{t('global.duration')}</p>
              </div>
              <span>
                {formatDurationTimeSpan(
                  detail.duration,
                  detail.duration ? TIME_DURATION_FORMAT : HOURS_DURATION_FORMAT,
                )}
              </span>
            </th>
            <th scope="col">
              <div className="header text-muted d-flex align-items-center justify-content-center">
                <DollarSignFilledIcon size={20} />
                <p className="ms-1 mb-0">{t('timecards.paidTime')}</p>
              </div>
              <span>
                {formatDurationTimeSpan(detail.paid, detail.paid ? TIME_DURATION_FORMAT : HOURS_DURATION_FORMAT)}
              </span>
            </th>
          </tr>
        </thead>
      </table>
      <Collapse in={openDetails} timeout="auto" unmountOnExit>
        <div
          className="rounded d-flex align-items-start justify-content-between p-3 mx-3"
          style={{ border: `1px dashed ${theme.palette.mode === 'light' ? '#dee2e6' : '#6C757D'}` }}
        >
          {detail.isEmptyDay ? (
            <p className="text-muted mb-0 small fst-italic">{t('global.noData')}</p>
          ) : (
            <table className="timecard-shift-details-table">
              <tbody>
                <tr>
                  <td>
                    <p className="small fw-400 mb-0 text-muted me-3">
                      {t('timecards.schedule')} {t('global.start')} - {t('global.end')}:
                    </p>
                  </td>
                  <td>
                    <p className="small fw-400 mb-0">
                      <TimecardTimeSpanValue startTimeSpan={detail.scheduledStart} endTimeSpan={detail.scheduledEnd} />
                    </p>
                  </td>
                </tr>
                <tr>
                  <td>
                    <p className="small fw-400 mb-0 text-muted me-3">
                      {t('timecards.actual')} {t('timecards.in')} - {t('timecards.out')}:
                    </p>
                  </td>
                  <td>
                    <p className="small fw-400 mb-0">
                      <TimecardTimeSpanValue startTimeSpan={detail.in} endTimeSpan={detail.out} />
                    </p>
                  </td>
                </tr>
                <tr>
                  <td>
                    <p className="small fw-400 mb-0 text-muted me-3">
                      {t('timecards.paid')} {t('global.start')} - {t('global.end')}:
                    </p>
                  </td>
                  <td>
                    <p className="small fw-400 mb-0">
                      <TimecardTimeSpanValue startTimeSpan={detail.paidStart} endTimeSpan={detail.paidEnd} />
                    </p>
                  </td>
                </tr>
                {premiumCategories.map((category) => (
                  <tr key={`timecard-premium-category-${category.timeCategoryId}`}>
                    <td>
                      <p className="small fw-400 mb-0 text-muted me-3">{category.timeCategoryDescription}:</p>
                    </td>
                    <td>
                      <p className="small fw-400 mb-0">
                        {formatDuration(category.hours, DurationFormatEnums.T_24_HOURS)}
                      </p>
                    </td>
                  </tr>
                ))}
                {memoCategories.map((category) => (
                  <tr key={`timecard-memo-category-${category.timeCategoryId}`}>
                    <td>
                      <p className="small fw-400 mb-0 text-muted me-3">{category.timeCategoryDescription}:</p>
                    </td>
                    <td>
                      <p className="small fw-400 mb-0">${category.memoAmount}</p>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div>
            {categories.map((category) => {
              const borderStyle = !category.isPaid
                ? { border: '1px dashed #6C757D' }
                : { border: `1px solid ${theme.palette.primary.main}` };

              return (
                <div
                  key={`timecard-category-${category.timeCategoryId}`}
                  style={borderStyle}
                  className="timecard-shift-details-types small mb-1 m-auto me-0"
                >
                  <span className="text-muted me-2">{category.timeCategoryDescription}:</span>
                  {formatDuration(category.hours, DurationFormatEnums.T_24_HOURS)}
                </div>
              );
            })}
          </div>
        </div>
      </Collapse>
      <Button
        startIcon={<ChevronDownIcon />}
        className={`m-3 timecard-details-btn ${openDetails ? 'open' : ''}`}
        color="secondary"
        onClick={() => setOpenDetails(!openDetails)}
      >
        {openDetailsButtonText}
      </Button>
    </Paper>
  );
};

const TimecardTimeSpanValue: FC<{
  startTimeSpan: string | null;
  endTimeSpan: string | null;
}> = ({ startTimeSpan, endTimeSpan }) => {
  const { t } = useTranslate();
  const timeFormat = useSelector(getTimeFormat);

  const noDataElement = <span className="text-muted small fst-italic">{t('global.noData')}</span>;

  if (startTimeSpan && !endTimeSpan) {
    return (
      <span>
        {formatTime(startTimeSpan, timeFormat)} - {noDataElement}
      </span>
    );
  }

  if (!startTimeSpan && endTimeSpan) {
    return (
      <span>
        {noDataElement} - {formatTime(endTimeSpan, timeFormat)}
      </span>
    );
  }

  if (startTimeSpan && endTimeSpan) {
    return (
      <span>
        {formatTime(startTimeSpan, timeFormat)} - {formatTime(endTimeSpan, timeFormat)}
      </span>
    );
  }

  return noDataElement;
};
